import { useEffect, useState } from 'react';
import { useStore } from '../state/store';
import { logout, changePassword, CloudError } from '../lib/cloud';

/** Shared account dropdown, rendered in the studio header and the home page. */
export function AccountMenu() {
  const user = useStore((s) => s.auth.user)!;
  const setAuth = useStore((s) => s.setAuth);
  const setView = useStore((s) => s.setView);
  const pushLine = useStore((s) => s.pushLine);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function signOut(): void {
    void logout();
    setAuth(null, null);
    setOpen(false);
    setView('home');
    pushLine('info', 'Signed out. This session is closed — any earlier session on another device stays invalid.');
  }

  function openPasswordModal(): void {
    setOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwError(null);
    setPwOpen(true);
  }

  function submitPasswordChange(): void {
    setPwError(null);
    if (newPassword.length < 4) {
      setPwError('New password must be at least 4 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('The new passwords do not match.');
      return;
    }
    void (async () => {
      setPwBusy(true);
      try {
        await changePassword(currentPassword, newPassword);
        setPwOpen(false);
        pushLine('ok', 'Password changed.');
      } catch (err) {
        setPwError(err instanceof CloudError ? err.message : 'Could not change the password.');
      } finally {
        setPwBusy(false);
      }
    })();
  }

  const roleLabel =
    user.role === 'main_admin'
      ? 'SPadministrator'
      : user.role === 'org_admin'
        ? 'Administrator (org)'
        : user.role === 'org_manager'
          ? 'Manager (org)'
          : 'Member (org)';

  const displayName = user.name || user.email || user.username || 'Account';

  return (
    <div className="account-wrap">
      <button className="btn btn-account" onClick={() => setOpen((v) => !v)} title="Account">
        <span className="account-avatar">{displayName.charAt(0).toUpperCase()}</span>
        {displayName}
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="account-menu" role="menu">
            <div className="account-menu-head">
              <div className="account-menu-email">{displayName}</div>
              <div className="account-menu-role">
                {roleLabel}
                {user.orgName ? ` · ${user.orgName}` : ''}
              </div>
            </div>
            <button className="menu-item" onClick={() => { setOpen(false); setView('home'); }}>
              My projects
            </button>
            <button className="menu-item" onClick={openPasswordModal}>
              Change password
            </button>
            {(user.role === 'org_admin' || user.role === 'org_manager') && (
              <button className="menu-item" onClick={() => { setOpen(false); setView('org'); }}>
                Organization
              </button>
            )}
            {user.isAdmin && (
              <button className="menu-item" onClick={() => { setOpen(false); setView('admin'); }}>
                Admin console
              </button>
            )}
            <button className="menu-item danger" onClick={signOut}>
              Sign out
            </button>
          </div>
        </>
      )}

      {pwOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Change password">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-head">
              <div className="modal-title">Change password</div>
              <button className="modal-x" aria-label="Close" onClick={() => setPwOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <div className="field-label"><span>Current password</span></div>
                <input
                  type="password"
                  value={currentPassword}
                  autoComplete="current-password"
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="field">
                <div className="field-label"><span>New password</span></div>
                <input
                  type="password"
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="field">
                <div className="field-label"><span>Confirm new password</span></div>
                <input
                  type="password"
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitPasswordChange(); }}
                />
              </div>
              {pwError && <div className="form-error err">{pwError}</div>}
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setPwOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submitPasswordChange} disabled={pwBusy}>
                {pwBusy ? 'Working…' : 'Change password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
